import React, { useEffect, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import API_URL from "../config";

const GestionarRespaldos = () => {
  const [config, setConfig] = useState(null);
  const [historial, setHistorial] = useState([]);

  const [tipo, setTipo] = useState("ambos");
  const [frecuencia, setFrecuencia] = useState("manual");
  const [hora, setHora] = useState("02:00");
  const [activo, setActivo] = useState(false);

  const [guardando, setGuardando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);

  const cargar = async () => {
    const res = await axios.get(`${API_URL}/api/respaldos`);
    const c = res.data.config;
    if (c) {
      setConfig(c);
      setTipo(c.tipo);
      setFrecuencia(c.frecuencia);
      setHora((c.hora || "02:00:00").slice(0, 5));
      setActivo(!!c.activo);
    }
    setHistorial(res.data.historial || []);
  };

  useEffect(() => {
    cargar();
  }, []);

  const guardarConfig = async () => {
    try {
      setGuardando(true);
      await axios.post(`${API_URL}/api/respaldos`, {
        tipo,
        frecuencia,
        hora: `${hora}:00`,
        activo,
      });
      await cargar();
      alert("✅ Configuración guardada");
    } catch (err) {
      alert(err.response?.data?.error || "❌ No se pudo guardar la configuración");
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarAhora = async (tipoEjecutar) => {
    if (!window.confirm("Esto va a generar el respaldo ahora mismo en el servidor. ¿Continuar?")) return;
    try {
      setEjecutando(true);
      await axios.post(`${API_URL}/api/respaldos/ejecutar`, { tipo: tipoEjecutar });
      await cargar();
      alert("✅ Respaldo generado correctamente");
    } catch (err) {
      alert(err.response?.data?.error || "❌ El respaldo falló");
    } finally {
      setEjecutando(false);
    }
  };

  const formatearTamano = (bytes) => {
    if (!bytes) return "—";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div className="p-3 md:p-6 md:max-w-3xl">
      <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center md:text-left">
        💾 Respaldos del Sistema
      </h3>
      <p className="text-sm text-gray-500 mb-6 -mt-2">
        Configura respaldos automáticos de la base de datos y/o la carpeta del proyecto, guardados
        en el propio servidor. También puedes generar uno de inmediato cuando quieras.
      </p>

      {/* ================= EJECUTAR AHORA ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-3">Ejecutar ahora</h4>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => ejecutarAhora("bd")}
            disabled={ejecutando}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
          >
            {ejecutando ? "Generando..." : "🗄️ Solo Base de Datos"}
          </button>
          <button
            onClick={() => ejecutarAhora("carpeta")}
            disabled={ejecutando}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
          >
            {ejecutando ? "Generando..." : "📁 Solo Carpeta del Proyecto"}
          </button>
          <button
            onClick={() => ejecutarAhora("ambos")}
            disabled={ejecutando}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
          >
            {ejecutando ? "Generando..." : "💾 Ambos"}
          </button>
        </div>
      </div>

      {/* ================= CONFIGURACIÓN AUTOMÁTICA ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-3">Respaldo automático</h4>

        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            Activar respaldo automático
          </label>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de respaldo</label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={tipo === "bd"} onChange={() => setTipo("bd")} />
              Solo Base de Datos
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={tipo === "carpeta"} onChange={() => setTipo("carpeta")} />
              Solo Carpeta del Proyecto
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={tipo === "ambos"} onChange={() => setTipo("ambos")} />
              Ambos
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Frecuencia</label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={frecuencia === "diario"} onChange={() => setFrecuencia("diario")} />
              Diario
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={frecuencia === "mensual"} onChange={() => setFrecuencia("mensual")} />
              Mensual (día 1)
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={frecuencia === "manual"} onChange={() => setFrecuencia("manual")} />
              Solo manual (sin automático)
            </label>
          </div>
        </div>

        {frecuencia !== "manual" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora de ejecución</label>
            <input
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              className="border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        )}

        <button
          onClick={guardarConfig}
          disabled={guardando}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold transition"
        >
          {guardando ? "Guardando..." : "💾 Guardar configuración"}
        </button>

        {config?.ultima_ejecucion && (
          <p className="text-xs text-gray-500 mt-3">
            Última ejecución automática:{" "}
            {DateTime.fromISO(config.ultima_ejecucion, { zone: "utc" })
              .toLocal()
              .toFormat("dd/MM/yyyy hh:mm a")}
          </p>
        )}
      </div>

      {/* ================= HISTORIAL ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5">
        <h4 className="font-bold text-slate-700 mb-3">Historial reciente</h4>

        {historial.length === 0 ? (
          <p className="text-gray-500 text-sm">Todavía no se ha generado ningún respaldo.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {historial.map((h) => (
              <div
                key={h.id}
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 bg-slate-50 rounded-lg p-3"
                style={{ borderLeft: `4px solid ${h.exitoso ? "#22c55e" : "#ef4444"}` }}
              >
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{h.archivo}</p>
                  <p className="text-xs text-gray-500">
                    {DateTime.fromISO(h.creado_en, { zone: "utc" }).toLocal().toFormat("dd/MM/yyyy hh:mm a")}
                    {" · "}
                    {formatearTamano(h.tamano_bytes)}
                    {!h.exitoso && h.mensaje_error ? ` · ${h.mensaje_error}` : ""}
                  </p>
                </div>
                <span
                  className={`text-xs font-bold px-2 py-1 rounded-full w-fit ${
                    h.exitoso ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}
                >
                  {h.exitoso ? "Exitoso" : "Falló"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionarRespaldos;
